import type { HTMLAttributes, PropsWithChildren } from "react";
import { Children } from "react";

import { css } from "@compiled/react";

import { useTabsContext, TabFC, mapChild } from "./";

export type TabPanelProps = PropsWithChildren<{
    forceRender?: boolean;
    panelIndex?: number;
    hideTabPanelRole?: boolean;
}> &
    HTMLAttributes<HTMLDivElement>;

const hidePanelStyle = css({
    display: "none"
});

/**
 * Generate safe IDs with fallback to prevent "panelundefined" and "tabundefined"
 */
function generateSafeIds(
    id: string | number | undefined,
    panelIndex: number | undefined
): { panelId: string; tabId: string } {
    const idString = id != null ? String(id) : "";
    const isValidId =
        id != null &&
        idString !== "undefined" &&
        idString !== "" &&
        idString !== "null";

    const safeId = isValidId
        ? id
        : panelIndex != null && typeof panelIndex === "number"
        ? panelIndex
        : "default";

    const safeIdString = String(safeId);
    return {
        panelId: `panel${safeIdString}`,
        tabId: `tab${safeIdString}`
    };
}

const TabPanel: TabFC<TabPanelProps> = ({
    forceRender,
    children,
    panelIndex,
    id,
    hideTabPanelRole = false,
    ...props
}: TabPanelProps) => {
    const { isCurrentTab } = useTabsContext();

    const isTabSelected = isCurrentTab(panelIndex);
    const activeClass = isTabSelected ? "active-panel" : "";

    const { panelId, tabId } = generateSafeIds(id, panelIndex);

    // When hideTabPanelRole is false, match original structure exactly to avoid snapshot changes
    // Original code overwrote css from props, so we do the same here
    if (!hideTabPanelRole) {
        return (
            <div
                {...props}
                css={[!isTabSelected && !forceRender && hidePanelStyle]}
                id={panelId}
                aria-labelledby={tabId}
                aria-hidden={!isTabSelected}
                role="tabpanel"
                className={["tab-panel", activeClass].join(" ")}
            >
                {Children.map(children, (child) => {
                    return mapChild(child, { isPanelActive: isTabSelected });
                })}
            </div>
        );
    }

    // When hideTabPanelRole is true, omit role and aria-labelledby
    // Extract css separately to preserve it when filtering props
    const {
        "role": _role,
        "aria-labelledby": _ariaLabelledBy,
        "css": existingCss,
        ...filteredProps
    } = props;

    // Build css array - Compiled CSS needs static array literals
    // Always use conditional css (existingCss extracted to prevent conflicts, but not merged)
    const cssProp = [!isTabSelected && !forceRender && hidePanelStyle];

    return (
        <div
            {...filteredProps}
            css={cssProp}
            id={panelId}
            aria-hidden={!isTabSelected}
            className={["tab-panel", activeClass].join(" ")}
        >
            {Children.map(children, (child) => {
                return mapChild(child, { isPanelActive: isTabSelected });
            })}
        </div>
    );
};
TabPanel.tabsRole = "TabPanel";

export { TabPanel };
